import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import resetBillingAddressFormValidation from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ResetBillingAddressFormValidation";
import validateBillingAddressForm from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ValidateBillingAddressForm";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
} from "@insite/client-framework/Types/ApiModels";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import { TextFieldProps } from "@insite/mobius/TextField";
import { formatPhoneNumber } from "@tCooneyBrothers/Common/Utilities/formatPhoneNumber";
import setBillingAddressUpdate from "@tCooneyBrothers/Handlers/SetBillingAddressUpdate";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@tCooneyBrothers/Overrides/Components/CustomerAddressFormFields";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    extendedStyles?: BillingAddressFormStyles;
    currentUserIsGuest?: boolean;
}

const mapStateToProps = ({
    pages: {
        checkoutShipping: { billingAddressFormState },
    },
}: ApplicationState) => ({
    billingAddressFormState,
});

const mapDispatchToProps = {
    setAddress: (address: BillToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetBillingAddress",
            address,
        });
    },
    validateForm: makeHandlerChainAwaitable<Parameters<typeof validateBillingAddressForm>[0], boolean>(
        validateBillingAddressForm,
    ),
    resetBillingAddressFormValidation,
    setBillingAddressUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BillingAddressFormStyles {
    formFields?: CustomerAddressFormFieldStyles;
}

export const billingAddressFormStyles: BillingAddressFormStyles = {};

/**
 * @deprecated Use billingAddressFormStyles instead.
 */
export const oneTimeAddressFormStyles = billingAddressFormStyles;
const styles = billingAddressFormStyles;

interface State {
    updateBillingAddress: boolean;
    styles: any;
}

class BillingAddressForm extends React.Component<Props, State, { styles: BillingAddressFormStyles }> {
    static contextType = CheckoutShippingFormContext;

    declare context: React.ContextType<typeof CheckoutShippingFormContext>;

    constructor(props: Props) {
        super(props);
        const { extendedStyles } = this.props;
        this.state = {
            styles: mergeToNew(styles, extendedStyles),
            updateBillingAddress: false,
        };
    }

    componentDidMount() {
        const { billingAddressFormState, fieldDisplay, validateForm } = this.props;
        const { validators } = this.context;
        if (!billingAddressFormState) {
            return;
        }
        const { address } = billingAddressFormState;
        const { validation } = address;
        if (!validation) {
            return;
        }
        validators.billingAddress = () =>
            validateForm({
                address,
                validation,
                fieldDisplay,
            });
    }

    componentWillUnmount() {
        const { validators } = this.context;
        validators.billingAddress = undefined;
    }

    componentDidUpdate(prevProps: Props) {
        const { validators, formSubmitAttempted } = this.context;
        const { billingAddressFormState: prevBillingAddressFormState, fieldDisplay: prevFieldDisplay } = prevProps;
        const { billingAddressFormState, fieldDisplay, validateForm } = this.props;
        if (prevBillingAddressFormState !== billingAddressFormState && billingAddressFormState) {
            const { address } = billingAddressFormState;
            const { validation } = address;
            if (
                (!prevBillingAddressFormState ||
                    prevBillingAddressFormState.address !== address ||
                    prevBillingAddressFormState.address.validation !== validation ||
                    prevFieldDisplay !== fieldDisplay) &&
                validation
            ) {
                validators.billingAddress = () =>
                    validateForm({
                        address,
                        validation,
                        fieldDisplay,
                    });

                if (formSubmitAttempted) {
                    validateForm({
                        address,
                        validation,
                        fieldDisplay,
                    });
                }
            }
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        this.props.setBillingAddressUpdate({ updateBillingAddress: true });
        const { billingAddressFormState, setAddress } = this.props;
        if (!billingAddressFormState) {
            return;
        }
        const { address } = billingAddressFormState;
        const selectedCountry = this.props.countries.find(c => c.id === address.country?.id);
        const isNorthAmericanCountry = selectedCountry?.abbreviation === "US" || selectedCountry?.abbreviation === "CA";
        const propertyValue =
            event.target.name === "phone"
                ? isNorthAmericanCountry
                    ? formatPhoneNumber(event.target.value.replace(/\D/g, ""))
                    : event.target.value.replace(/\D/g, "")
                : event.target.value;
        setAddress({
            ...address,
            [event.target.name]: propertyValue,
        });
    };

    handleInputBlur: TextFieldProps["onBlur"] = event => {
        const { billingAddressFormState, setAddress } = this.props;
        if (!billingAddressFormState) {
            return;
        }
        const selectedCountry = this.props.countries.find(c => c.id === address.country?.id);
        const isNorthAmericanCountry = selectedCountry?.abbreviation === "US" || selectedCountry?.abbreviation === "CA";
        const propertyValue =
            event.target.name === "phone"
                ? isNorthAmericanCountry
                    ? formatPhoneNumber(event.target.value.replace(/\D/g, ""))
                    : event.target.value.replace(/\D/g, "")
                : event.target.value;
        const { address } = billingAddressFormState;
        setAddress({
            ...address,
            [event.target.name]: propertyValue.trim(),
        });
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        this.props.setBillingAddressUpdate({ updateBillingAddress: true });
        const { billingAddressFormState, setAddress } = this.props;
        if (!billingAddressFormState) {
            return;
        }
        const { address } = billingAddressFormState;
        !country?.states || country.states.length === 0
            ? setAddress({
                  ...address,
                  country,
                  state: null,
                  phone: "",
              })
            : setAddress({
                  ...address,
                  country,
                  phone: "",
              });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        this.props.setBillingAddressUpdate({ updateBillingAddress: true });
        const { billingAddressFormState, setAddress } = this.props;
        if (!billingAddressFormState) {
            return;
        }
        const { address } = billingAddressFormState;
        setAddress({
            ...address,
            state,
        });
    };

    render() {
        const { billingAddressFormState, countries, fieldDisplay, currentUserIsGuest } = this.props;
        const { styles } = this.state;
        if (!billingAddressFormState) {
            return null;
        }
        const { address, formErrors } = billingAddressFormState;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
            validation,
        } = address;
        if (!validation) {
            return null;
        }
        return (
            <CustomerAddressFormFields
                firstName={firstName}
                lastName={lastName}
                attention={attention}
                companyName={companyName}
                address1={address1}
                address2={address2}
                address3={address3}
                address4={address4}
                country={country}
                city={city}
                state={state}
                postalCode={postalCode}
                phone={phone}
                email={email}
                fax={fax}
                currentUserIsGuest={currentUserIsGuest}
                onChangeFirstName={this.handleInputChange}
                onChangeLastName={this.handleInputChange}
                onChangeAttention={this.handleInputChange}
                onChangeCompanyName={this.handleInputChange}
                onChangeAddress1={this.handleInputChange}
                onChangeAddress2={this.handleInputChange}
                onChangeAddress3={this.handleInputChange}
                onChangeAddress4={this.handleInputChange}
                onBlurFirstName={this.handleInputBlur}
                onBlurLastName={this.handleInputBlur}
                onBlurAttention={this.handleInputBlur}
                onBlurCompanyName={this.handleInputBlur}
                onBlurAddress1={this.handleInputBlur}
                onBlurAddress2={this.handleInputBlur}
                onBlurAddress3={this.handleInputBlur}
                onBlurAddress4={this.handleInputBlur}
                onChangeCountry={this.handleCountryChange}
                onChangeState={this.handleStateChange}
                onChangeCity={this.handleInputChange}
                onChangePostalCode={this.handleInputChange}
                onChangePhone={this.handleInputChange}
                onChangeEmail={this.handleInputChange}
                onChangeFax={this.handleInputChange}
                onBlurCity={this.handleInputBlur}
                onBlurPostalCode={this.handleInputBlur}
                onBlurPhone={this.handleInputBlur}
                onBlurEmail={this.handleInputBlur}
                onBlurFax={this.handleInputBlur}
                firstNameError={formErrors.firstName}
                lastNameError={formErrors.lastName}
                attentionError={formErrors.attention}
                companyNameError={formErrors.companyName}
                address1Error={formErrors.address1}
                address2Error={formErrors.address2}
                address3Error={formErrors.address3}
                address4Error={formErrors.address4}
                countryError={formErrors.country}
                stateError={formErrors.state}
                cityError={formErrors.city}
                postalCodeError={formErrors.postalCode}
                phoneError={formErrors.phone}
                emailError={formErrors.email}
                faxError={formErrors.fax}
                countries={countries}
                validation={validation}
                fieldDisplay={fieldDisplay}
                extendedStyles={styles.formFields}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(BillingAddressForm);
